// ════════════════════════════════════════════════════════════════════════════
// 🛡️ CashierAuth — Phase 4.2 Cashier login modal (password or PIN)
// ════════════════════════════════════════════════════════════════════════════
// Supports two modes:
//   - Full login (username + password)
//   - Quick PIN (username + 4-6 digit PIN)
// On success, stores JWT in localStorage and calls onAuth with session.
// ════════════════════════════════════════════════════════════════════════════

import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import { X, User, Lock, Hash, Loader2, Shield, Eye, EyeOff, Clock, CheckCircle, XCircle } from 'lucide-react';
import { Haptics } from '../../utils/haptics';

export interface CashierSession {
  id: number;
  storeId: number;
  username: string;
  name: string;
  role: 'manager' | 'cashier' | 'inventory' | 'readonly';
  roleLabel: string;
  customPermissions?: string[] | null;
}

interface CashierAuthProps {
  open: boolean;
  storeApiKey: string;
  onClose: () => void;
  onAuth: (session: CashierSession, token: string) => void;
  mode?: 'login' | 'pin';
}

const CashierAuth: React.FC<CashierAuthProps> = ({ open, storeApiKey, onClose, onAuth, mode = 'login' }) => {
  const [view, setView] = useState<'login' | 'pin'>(mode);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [pin, setPin] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pendingReqId, setPendingReqId] = useState<number | null>(null);
  const [pendingCashier, setPendingCashier] = useState<any>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const usernameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setUsername('');
      setPassword('');
      setPin('');
      setError('');
      setLoading(false);
      setPendingReqId(null);
      setPendingCashier(null);
      if (pollRef.current) clearInterval(pollRef.current);
      setTimeout(() => usernameRef.current?.focus(), 100);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [open]);

  const startPolling = (reqId: number, cashierInfo: any) => {
    setPendingReqId(reqId);
    setPendingCashier(cashierInfo);
    pollRef.current = setInterval(async () => {
      try {
        const r = await fetch(`/api/cashier/login-request/${reqId}`);
        const d = await r.json();
        if (d.status === 'APPROVED' && d.token) {
          clearInterval(pollRef.current!);
          localStorage.setItem('kangavena:cashierToken', d.token);
          Haptics.fire('success');
          // 🎯 Extract customPermissions from JWT payload for immediate UI enforcement
          try {
            const parts = d.token.split('.');
            if (parts.length === 3) {
              const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
              if (payload.customPermissions) cashierInfo.customPermissions = payload.customPermissions;
            }
          } catch { /* JWT decode best-effort — /api/cashier/me will correct it */ }
          onAuth(cashierInfo, d.token);
        } else if (d.status === 'REJECTED') {
          clearInterval(pollRef.current!);
          setPendingReqId(null);
          setPendingCashier(null);
          setError('Login rejected by manager.');
          Haptics.fire('warn');
        } else if (d.status === 'EXPIRED') {
          clearInterval(pollRef.current!);
          setPendingReqId(null);
          setPendingCashier(null);
          setError('Approval request expired. Please try again.');
        }
      } catch { /* ignore poll network errors */ }
    }, 2500);
  };

  const submit = async () => {
    setError('');
    if (!username.trim()) { setError('Username is required'); return; }
    if (view === 'login' && !password) { setError('Password is required'); return; }
    if (view === 'pin' && pin.length < 4) { setError('PIN must be at least 4 digits'); return; }

    setLoading(true);
    try {
      const endpoint = view === 'login' ? '/api/cashier/login' : '/api/cashier/login-pin';
      const body = view === 'login'
        ? { storeApiKey, username: username.trim(), password }
        : { storeApiKey, username: username.trim(), pin };

      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Authentication failed');
        Haptics.fire('warn');
        return;
      }
      // Phase 6 — Manager Approval flow
      if (data.pending && data.requestId) {
        Haptics.fire('success');
        startPolling(data.requestId, data.cashier);
        return;
      }
      Haptics.fire('success');
      localStorage.setItem('kangavena:cashierToken', data.token);
      onAuth(data.cashier, data.token);
    } catch (err: any) {
      setError(err?.message || 'Network error');
      Haptics.fire('warn');
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return createPortal(
    <AnimatePresence>
      <motion.div
        key="ca-bg"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[10002] bg-slate-900/80 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      >
        <motion.div
          key="ca-card"
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          transition={{ type: 'spring', stiffness: 380, damping: 32 }}
          className="w-full max-w-sm bg-white rounded-3xl shadow-2xl border border-slate-200 overflow-hidden"
        >
          {/* Header */}
          <div className="px-5 pt-5 pb-3 border-b border-slate-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center text-white">
                <Shield size={16} />
              </div>
              <div>
                <h3 className="text-[14px] font-black text-slate-800">Cashier Sign In</h3>
                <p className="text-[10px] text-slate-500">{view === 'login' ? 'Username + Password' : 'Quick PIN'}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-700"
              aria-label="Close"
            >
              <X size={15} />
            </button>
          </div>

          {/* ── Pending Approval State ── */}
          {pendingReqId && pendingCashier && (
            <div className="p-6 text-center space-y-4">
              <div className="flex items-center justify-center">
                <div className="relative">
                  <div className="w-16 h-16 rounded-full bg-amber-50 border-2 border-amber-200 flex items-center justify-center">
                    <Clock size={28} className="text-amber-500 animate-pulse" />
                  </div>
                  <div className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-violet-100 border-2 border-white flex items-center justify-center">
                    <Loader2 size={12} className="text-violet-500 animate-spin" />
                  </div>
                </div>
              </div>
              <div>
                <p className="text-[15px] font-black text-slate-800">Waiting for approval</p>
                <p className="text-[11px] text-slate-500 mt-0.5">Hi <span className="font-bold text-violet-600">{pendingCashier.name || pendingCashier.username}</span>!</p>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-left space-y-1.5">
                <p className="text-[9px] font-black text-amber-700 uppercase tracking-widest">Pending at your manager's screen</p>
                <p className="text-[11px] text-slate-600">Your login request has been sent to the <span className="font-bold">store manager</span>. Please ask them to approve it on their POS dashboard.</p>
              </div>
              <button
                onClick={() => { if (pollRef.current) clearInterval(pollRef.current); setPendingReqId(null); setPendingCashier(null); }}
                className="w-full py-2.5 rounded-xl border border-slate-200 text-[12px] text-slate-500 hover:bg-slate-50 transition-colors font-bold"
              >
                Cancel
              </button>
            </div>
          )}

          {/* Tabs */}
          {!pendingReqId && <div className="flex px-5 pt-3 gap-2">
            <button
              onClick={() => { setView('login'); setError(''); }}
              className={`flex-1 py-2 rounded-xl text-[11px] font-black uppercase tracking-wider transition-all ${
                view === 'login'
                  ? 'bg-violet-100 text-violet-700 border border-violet-200'
                  : 'bg-slate-50 text-slate-500 border border-slate-100 hover:bg-slate-100'
              }`}
            >
              Password
            </button>
            <button
              onClick={() => { setView('pin'); setError(''); }}
              className={`flex-1 py-2 rounded-xl text-[11px] font-black uppercase tracking-wider transition-all ${
                view === 'pin'
                  ? 'bg-violet-100 text-violet-700 border border-violet-200'
                  : 'bg-slate-50 text-slate-500 border border-slate-100 hover:bg-slate-100'
              }`}
            >
              Quick PIN
            </button>
          </div>}

          {/* Form */}
          {!pendingReqId &&
          <div className="p-5 space-y-3">
            <div className="space-y-1">
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
                <User size={10} /> Username
              </label>
              <input
                ref={usernameRef}
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
                placeholder="your_username"
                className="w-full px-3 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-violet-400 focus:ring-2 focus:ring-violet-100 outline-none text-[13px] text-slate-800"
                autoFocus
              />
            </div>

            {view === 'login' ? (
              <div className="space-y-1">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
                  <Lock size={10} /> Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
                    placeholder="••••••"
                    className="w-full pl-3 pr-10 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-violet-400 focus:ring-2 focus:ring-violet-100 outline-none text-[13px] text-slate-800"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((s) => !s)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-1">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
                  <Hash size={10} /> PIN
                </label>
                <input
                  type="password"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
                  placeholder="4-6 digits"
                  className="w-full px-3 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-violet-400 focus:ring-2 focus:ring-violet-100 outline-none text-[13px] font-mono tracking-widest text-slate-800"
                />
              </div>
            )}

            {error && (
              <motion.div
                initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }}
                className="text-[11px] font-bold text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2"
              >
                {error}
              </motion.div>
            )}

            <motion.button
              whileTap={{ scale: 0.97 }}
              onClick={submit}
              disabled={loading || !username.trim() || (view === 'login' ? !password : pin.length < 4)}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-[12px] font-black uppercase tracking-widest shadow-lg shadow-violet-500/25 disabled:opacity-50 transition-all"
            >
              {loading ? <Loader2 size={14} className="animate-spin" /> : <Shield size={14} />}
              Sign In
            </motion.button>
          </div>}

          {/* Footer hint */}
          {!pendingReqId && <div className="px-5 pb-4 text-center">
            <p className="text-[9px] text-slate-400">
              Ask your store manager for an account.
            </p>
          </div>}
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};

export default CashierAuth;
